import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShoppingBag, FileText, Minus, Plus } from "lucide-react";
import StockBadge from "./StockBadge";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Spec = ({ label, value }) =>
    value ? (
        <div className="flex justify-between gap-4 py-2 border-b border-[#E2D4C1] last:border-0">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#6B645C]">{label}</span>
            <span className="text-sm text-[#1A1A1A] text-right">{value}</span>
        </div>
    ) : null;

export default function ProductQuickView({ product: p, open, onOpenChange, onRequestQuote }) {
    const { add } = useCart();
    const { isB2B, isAdmin } = useAuth();
    const [qty, setQty] = useState(1);
    if (!p) return null;
    const isWholesale = isB2B || isAdmin;
    const price = isWholesale && p.wholesale_price ? p.wholesale_price : p.price;
    const soldOut = p.stock_status === "out_of_stock";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-charcoal-800 border-[#E2D4C1] max-w-3xl rounded-none p-0 overflow-hidden" data-testid="quick-view-modal">
                <DialogTitle className="sr-only">{p.name}</DialogTitle>
                <DialogDescription className="sr-only">{p.description || "Product details"}</DialogDescription>
                <div className="grid md:grid-cols-2">
                    <div className="aspect-square bg-[#E2D4C1]">
                        {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-6 md:p-8 flex flex-col">
                        <div className="flex items-center gap-2 mb-3">
                            <StockBadge status={p.stock_status} />
                            {p.brand && <span className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C]">{p.brand}</span>}
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl mb-2">{p.name}</h2>
                        <div className="text-gold text-2xl font-medium mb-4">{p.price_range || fmt(price)}</div>
                        <p className="text-[#33302C] text-sm mb-5 leading-relaxed">{p.description}</p>

                        <div className="mb-5">
                            <Spec label="MBG SKU" value={p.mbg_sku} />
                            <Spec label="Supplier SKU" value={p.supplier_sku} />
                            <Spec label="Series" value={p.series} />
                            <Spec label="Film Type" value={p.film_type} />
                            <Spec label="VLT Options" value={(p.vlt_options || []).join(", ")} />
                            <Spec label="Roll Sizes" value={(p.roll_sizes || []).join(", ")} />
                            <Spec label="Heat Rejection" value={p.heat_rejection} />
                            <Spec label="UV Protection" value={p.uv_protection} />
                        </div>

                        <div className="mt-auto flex items-center gap-2">
                            <div className="flex items-center border border-[#E2D4C1]">
                                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-10 grid place-items-center text-[#33302C] hover:text-gold"><Minus size={13} /></button>
                                <span className="w-9 text-center text-sm">{qty}</span>
                                <button onClick={() => setQty(qty + 1)} className="w-9 h-10 grid place-items-center text-[#33302C] hover:text-gold"><Plus size={13} /></button>
                            </div>
                            <button
                                data-testid="qv-add-to-cart"
                                disabled={soldOut}
                                onClick={() => { add({ ...p, price }, qty); toast.success(`${p.name} added to cart`); onOpenChange(false); }}
                                className="flex-1 h-10 bg-gold hover:bg-gold-hover text-white uppercase tracking-[0.18em] text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors"
                            >
                                <ShoppingBag size={14} /> {soldOut ? "Sold Out" : "Add to Cart"}
                            </button>
                        </div>
                        <button
                            data-testid="qv-request-quote"
                            onClick={() => { onOpenChange(false); onRequestQuote?.(p); }}
                            className="mt-2 w-full h-10 border border-[#8C5A3C]/40 text-[#33302C] hover:border-gold hover:text-gold uppercase tracking-[0.18em] text-[11px] inline-flex items-center justify-center gap-1.5 transition-colors"
                        >
                            <FileText size={14} /> Request Quote
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
